import {
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
} from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import UpdatePlanModal from './UpdatePlanModal';
import PlanSelectionGrid from './PlanSelectionGrid';
import { useBilling } from '../../../store/client/BillingContext';
import { HttpContext } from '../../../context/HttpContext';
import CustomToast from '../../../common/CustomToast';
import { useStripe } from '@stripe/react-stripe-js';
import { MeContext } from '../../../context/MeContext';

const SubscriptionComparisonModal = ({
  isOpen,
  onClose,
  onOpenRequestCustomPlan,
  customPlanRequest,
}) => {
  const stripe = useStripe();
  const { authAxios } = useContext(HttpContext);
  const { subscription, paymentMethod, updateSubscription, refresh } =
    useBilling();
  const updatePlanModal = useDisclosure();
  const [selectedPlan, setSelectedPlan] = useState({});
  const [cancelPlan, setCancelPlan] = useState(false);
  const [subscriptionOptions, setSubscriptionOptions] = useState([]);
  const [currentPlanData, setCurrentPlanData] = useState(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const toast = useToast();
  const [selectedInterval, setSelectedInterval] = useState('year');

  const staticPlans = useMemo(
    () => [
      {
        id: 'custom',
        name: 'Custom',
        buttonText: 'Request a plan',
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        buttonText: 'Contact sales',
      },
    ],
    []
  );

  const account = useContext(MeContext).state.account;

  const handleBookDemo = () => {
    const name = `${account.first_name} ${account.last_name}`;
    const email = account.email;
    const url = `https://calendly.com/passentrydemo/30min?name=${name}&email=${email}`;
    if (window.Calendly && window.Calendly.initPopupWidget) {
      window.Calendly.initPopupWidget({ url });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedInterval(subscription?.plan?.interval || 'year');
    }
  }, [isOpen, subscription?.plan?.interval]);

  useEffect(() => {
    const fetchAvailablePlans = async () => {
      setIsLoadingPlans(true);
      try {
        const allPlans = await authAxios.get(
          '/internal/v1/billing/subscription_plans'
        );

        const currentPlan = allPlans.data.find(
          (plan) => plan.name === subscription?.plan?.name
        );

        if (currentPlan && !subscription?.canResubscribe) {
          const planFeatures =
            currentPlan.features?.length > 0 ? currentPlan.features : [];
          setCurrentPlanData({
            ...currentPlan,
            features: planFeatures,
          });
        } else {
          setCurrentPlanData(null);
        }

        setSubscriptionOptions(allPlans.data);
      } catch (error) {
        console.error('Error fetching available plans:', error);
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Error"
              description="Something went wrong. Please try again."
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchAvailablePlans();
  }, [authAxios, subscription?.plan?.name, subscription?.status, toast]);

  useEffect(() => {
    if (currentPlanData) {
      const planFeaturesList = document.querySelector('#plan-features-list');
      if (planFeaturesList) {
        planFeaturesList.innerHTML = '';

        (currentPlanData.features || []).forEach((feature) => {
          const featureText =
            typeof feature === 'object' ? feature.name : feature;
          const li = document.createElement('li');
          li.textContent = featureText;
          planFeaturesList.appendChild(li);
        });
      }
    }
  }, [currentPlanData]);

  const handleIntervalChange = (interval) => {
    setSelectedInterval(interval);
    setSelectedPlan({});
  };

  const handleSubscribe = async () => {
    try {
      const apiUrl = `/internal/v1/billing/subscriptions/prepare_upgrade`;
      const confirmationUrl = `/internal/v1/billing/subscriptions/confirm_upgrade`;

      const apiData = {
        price_id: selectedPlan.priceId,
      };

      let response;
      try {
        response = await authAxios.post(apiUrl, apiData);
      } catch (apiError) {
        console.error('API call failed with error:', apiError);
        throw apiError;
      }

      const {
        data: {
          requires_action: requiresAction,
          client_secret: clientSecret,
          payment_method_id: paymentMethodId,
          stripe_subscription_id: stripeSubscriptionId,
        },
      } = response;

      if (requiresAction) {
        await stripe
          .confirmPayment({
            clientSecret,
            confirmParams: {
              payment_method: paymentMethodId,
              return_url: window.location.href,
            },
            redirect: 'if_required',
          })
          .then(async (result) => {
            response = await authAxios.post(confirmationUrl, {
              success: !result.error,
              stripe_subscription_id: stripeSubscriptionId,
              error: result.error,
            });
          });
      } else {
        response = await authAxios.post(confirmationUrl, {
          success: true,
          stripe_subscription_id: stripeSubscriptionId,
        });
      }

      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Subscription updated successfully"
            description=""
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });

      const { subscription: updatedSubscription } = response.data;

      if (updatedSubscription) {
        updateSubscription(updatedSubscription);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors[0];

      if (errorMessage) {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Error"
              description={errorMessage}
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      } else {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Error"
              description="Something went wrong"
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      }
    }
  };

  const planCardCount =
    (currentPlanData ? 1 : 0) + subscriptionOptions.length + staticPlans.length;
  const comparisonModalSize =
    planCardCount <= 2 ? '4xl' : planCardCount <= 3 ? '5xl' : '6xl';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={!updatePlanModal.isOpen ? comparisonModalSize : 'xl'}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent pt={6} pb={12} px={4}>
        {!updatePlanModal.isOpen && (
          <>
            <ModalCloseButton />
            <ModalBody mb={4}>
              <PlanSelectionGrid
                plans={subscriptionOptions}
                isLoading={isLoadingPlans}
                selectedInterval={selectedInterval}
                onIntervalChange={handleIntervalChange}
                onPlanSelect={(option, price, planData) => {
                  setSelectedPlan(planData);
                }}
                onPlanSelected={updatePlanModal.onOpen}
                setCancelPlan={setCancelPlan}
                subscription={subscription}
                currentPlanData={currentPlanData}
                title="Choose your plan"
                staticPlans={staticPlans}
                customPlanRequest={customPlanRequest}
                onOpenRequestCustomPlan={onOpenRequestCustomPlan}
                handleBookDemo={handleBookDemo}
                showFooter
              />
            </ModalBody>
          </>
        )}

        {updatePlanModal.isOpen && (
          <UpdatePlanModal
            handleClose={() => {
              updatePlanModal.onClose();
            }}
            onSuccess={() => {
              onClose();
              if (refresh) {
                setTimeout(() => refresh(), 1000);
              }
            }}
            selectedPlan={selectedPlan}
            cancelPlan={cancelPlan}
            existingPaymentMethod={paymentMethod}
            handleSubscribe={handleSubscribe}
            subscription={subscription}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

export default SubscriptionComparisonModal;
